export interface PreviewCaptureDeps {
  minIntervalMs: number;
  uploadMedia: (file: File, name: string) => Promise<{ id: string | number }>;
  updatePreview: (layeredMediaId: number, previewMediaId: number) => Promise<any>;
  now?: () => number;
}

export function createPreviewCapture(deps: PreviewCaptureDeps) {
  const { minIntervalMs, uploadMedia, updatePreview, now = () => Date.now() } = deps;
  let lastCapture = -Infinity;

  return async function capture(
    layeredMediaId: number,
    mainLayer: any,
    composition: { width: number; height: number },
  ): Promise<void> {
    if (now() - lastCapture < minIntervalMs) return;
    lastCapture = now();

    const stage = mainLayer.getStage?.();
    const prevScale = stage?.scaleX?.() ?? 1;
    const prevX = stage?.x?.() ?? 0;
    const prevY = stage?.y?.() ?? 0;
    stage?.scale?.({ x: 1, y: 1 });
    stage?.position?.({ x: 0, y: 0 });

    const blob = await new Promise<Blob | null>((resolve) => {
      try {
        mainLayer.toBlob({
          x: 0,
          y: 0,
          width: composition.width,
          height: composition.height,
          mimeType: 'image/jpeg',
          quality: 0.7,
          pixelRatio: 0.25,
          callback: (b: Blob | null) => {
            stage?.scale?.({ x: prevScale, y: prevScale });
            stage?.position?.({ x: prevX, y: prevY });
            stage?.batchDraw?.();
            resolve(b);
          },
        });
      } catch {
        stage?.scale?.({ x: prevScale, y: prevScale });
        stage?.position?.({ x: prevX, y: prevY });
        stage?.batchDraw?.();
        resolve(null);
      }
    });

    if (!blob) return;
    const file = new File([blob], 'preview.jpg', { type: 'image/jpeg' });
    const uploaded = await uploadMedia(file, 'preview.jpg');
    const previewId = typeof uploaded.id === 'string' ? parseInt(uploaded.id, 10) : uploaded.id;
    if (!Number.isFinite(previewId)) return;
    await updatePreview(layeredMediaId, previewId);
  };
}
